import { API_KEY, framed, parentOrigin } from "./bridge.ts";
import { solveTurnstile } from "./turnstile.ts";
import type { SseEvent, WidgetConfig } from "./types.ts";

export const API_URL: string = import.meta.env.VITE_API_URL.replace(/\/$/, "");
export const API_ORIGIN = new URL(API_URL).origin;

/** Error with a sentence the visitor can read. */
export class ApiError extends Error {
  readonly status: number;
  /** The API's machine-readable reason, when it gives one ("pass_required"). */
  readonly code: string | undefined;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const OFFLINE = "Brak połączenia z asystentem. Sprawdź internet i spróbuj ponownie.";
const CHECK_FAILED =
  "Nie udało się uruchomić zabezpieczenia czatu. Jeśli blokujesz skrypty na tej stronie, zezwól na nie i spróbuj ponownie.";

/**
 * Where the visit came from (the kiosk: source and UTM of the QR link). Goes
 * with every chat message into the conversation and with every event.
 */
let visit: Record<string, string> = {};
export function setVisit(metadata: Record<string, string>) {
  visit = metadata;
}

/**
 * The page the chat is on, for the tenant's origin allowlist: the embedding
 * page when framed, the page itself when standalone (the kiosk).
 */
const embedOrigin = framed ? parentOrigin : location.origin;

function headers(json = false): HeadersInit {
  return {
    "X-API-Key": API_KEY,
    ...(embedOrigin ? { "X-Embed-Origin": embedOrigin } : {}),
    ...(json ? { "Content-Type": "application/json" } : {}),
  };
}

/** 429 and 403 carry full Polish sentences from the API; others get ours. */
async function failure(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
  if (body?.code === "pass_required") return new ApiError(401, body.error ?? CHECK_FAILED, body.code);
  if ((res.status === 429 || res.status === 403 || res.status === 503) && body?.error) return new ApiError(res.status, body.error);
  if (res.status === 401) return new ApiError(401, "Ten asystent nie jest jeszcze gotowy do rozmowy.");
  if (res.status === 400) return new ApiError(400, "Tej wiadomości nie udało się wysłać. Skróć ją i spróbuj ponownie.");
  if (res.status >= 500 && body?.error && res.status !== 500) return new ApiError(res.status, body.error);
  return new ApiError(res.status, "Asystent jest chwilowo niedostępny. Spróbuj ponownie za chwilę.");
}

async function request(path: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, init);
  } catch (error) {
    if ((error as Error).name === "AbortError") throw error;
    throw new ApiError(0, OFFLINE);
  }
  if (!res.ok) throw await failure(res);
  return res;
}

export async function fetchConfig(): Promise<WidgetConfig> {
  return (await request("/widget", { headers: headers() })).json();
}

// ---------------- chat pass (Cloudflare Turnstile) ----------------

let turnstile: WidgetConfig["turnstile"] = null;

/** From the widget config: whether conversations start with the check. */
export function configureTurnstile(config: WidgetConfig["turnstile"]) {
  turnstile = config ?? null;
}

const passKey = (sessionId: string) => `unitivo:pass:${sessionId}`;
const pending = new Map<string, Promise<string | null>>();

function storedPass(sessionId: string): string | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(passKey(sessionId)) ?? "null") as { pass: string; expires_at: string } | null;
    // A minute of margin: a pass must not expire between here and the API.
    return saved && Date.parse(saved.expires_at) - Date.now() > 60_000 ? saved.pass : null;
  } catch {
    return null;
  }
}

/**
 * The conversation's pass: the stored one, or a new one after the invisible
 * check. One check at a time per conversation, however many callers ask.
 */
function obtainPass(sessionId: string, fresh: boolean): Promise<string | null> {
  if (!turnstile) return Promise.resolve(null);
  if (!fresh) {
    const stored = storedPass(sessionId);
    if (stored) return Promise.resolve(stored);
  }
  const running = pending.get(sessionId);
  if (running) return running;
  const { siteKey, action } = turnstile;
  const promise = (async () => {
    let token: string;
    try {
      token = await solveTurnstile(siteKey, action, sessionId);
    } catch {
      throw new ApiError(0, CHECK_FAILED);
    }
    const res = await request("/session", { method: "POST", headers: headers(true), body: JSON.stringify({ session_id: sessionId, token }) });
    const body = (await res.json()) as { pass: string | null; expires_at: string | null };
    if (body.pass && body.expires_at) {
      try {
        sessionStorage.setItem(passKey(sessionId), JSON.stringify(body));
      } catch {
        // private mode: the pass lives for this page only
      }
    }
    return body.pass;
  })().finally(() => pending.delete(sessionId));
  pending.set(sessionId, promise);
  return promise;
}

/** Runs the check in the background when the chat opens, so the first message does not wait for it. */
export function prepareConversation(sessionId: string) {
  obtainPass(sessionId, false).catch(() => {});
}

/**
 * A request that carries the pass. A failed check is not fatal on the first
 * try (the API may not require passes yet); when the API asks for a pass,
 * a fresh check runs and the request goes once more, and then a failure shows.
 */
async function withPass(sessionId: string, send: (pass: string | null) => Promise<Response>): Promise<Response> {
  const pass = await obtainPass(sessionId, false).catch(() => null);
  try {
    return await send(pass);
  } catch (error) {
    if (!(error instanceof ApiError) || error.code !== "pass_required") throw error;
    try {
      sessionStorage.removeItem(passKey(sessionId));
    } catch {
      // nothing stored
    }
    return send(await obtainPass(sessionId, true));
  }
}

const passHeader = (pass: string | null): HeadersInit => (pass ? { "X-Chat-Pass": pass } : {});

export interface ChatRequest {
  message: string;
  session_id: string;
  context?: { url?: string; ref?: string };
}

export async function* streamChat(body: ChatRequest, signal: AbortSignal): AsyncGenerator<SseEvent> {
  const payload = Object.keys(visit).length ? { ...body, metadata: visit } : body;
  const res = await withPass(body.session_id, (pass) =>
    request("/chat", { method: "POST", headers: { ...headers(true), ...passHeader(pass) }, body: JSON.stringify(payload), signal }),
  );
  if (!res.body) throw new ApiError(0, OFFLINE);
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;
      // SSE events are separated by a blank line; keep the unfinished tail.
      const events = buffer.split("\n\n");
      buffer = events.pop() ?? "";
      for (const event of events) {
        const data = event
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart())
          .join("\n");
        if (!data) continue;
        try {
          yield JSON.parse(data) as SseEvent;
        } catch {
          // a malformed event is skipped, the stream goes on
        }
      }
    }
  } catch (error) {
    if ((error as Error).name === "AbortError") throw error;
    throw new ApiError(0, OFFLINE);
  } finally {
    reader.releaseLock();
  }
}

export interface LeadRequest {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  session_id: string;
  conversation_id?: string | null;
}

export async function submitLead(lead: LeadRequest): Promise<void> {
  await withPass(lead.session_id, (pass) =>
    request("/lead", { method: "POST", headers: { ...headers(true), ...passHeader(pass) }, body: JSON.stringify(lead) }),
  );
}

export type AnalyticsEvent =
  | "widget_loaded"
  | "suggestion_clicked"
  | "widget_opened"
  | "widget_closed"
  | "message_sent"
  | "message_received"
  | "property_clicked"
  | "plan_opened"
  | "answer_rated"
  | "lead_form_shown"
  | "lead_captured"
  | "whatsapp_clicked"
  | "error";

/** Fire and forget; never delays or breaks the chat. */
export function track(event: AnalyticsEvent, sessionId: string, metadata: Record<string, unknown> = {}, conversationId?: string | null) {
  const body = JSON.stringify({
    event,
    session_id: sessionId,
    ...(conversationId ? { conversation_id: conversationId } : {}),
    metadata: { ...visit, ...metadata },
  });
  fetch(`${API_URL}/analytics`, { method: "POST", headers: headers(true), body, keepalive: true }).catch(() => {});
}
